/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { PhotoIcon, XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getWallpaper, setWallpaper, Wallpaper } from "@/utils/wallpaper";
import { WALLPAPER_PRESETS } from "@/constants/wallpaperPresets";
import { showToast } from "@/components/common/Toast";
import { cn } from "@/lib/utils";

const MAX_UPLOAD_BYTES = 3 * 1024 * 1024; // 3MB -- localStorage is shared with
// dark mode, kiosk layout, tab state, etc; one oversized image could break them all.

export const WallpaperSettings: React.FC = () => {
  const [current, setCurrent] = useState<Wallpaper | null>(() => getWallpaper());
  const [urlInput, setUrlInput] = useState("");

  const apply = (wallpaper: Wallpaper | null) => {
    setWallpaper(wallpaper);
    setCurrent(wallpaper);
  };

  const handleUrlApply = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    apply({ type: "url", value: trimmed });
    showToast("Wallpaper updated", "success");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Invalid file", "error", { description: "Please choose an image file" });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      showToast("Image too large", "error", {
        description: "Please choose an image under 3MB",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        apply({ type: "upload", value: reader.result });
        showToast("Wallpaper updated", "success");
      }
    };
    reader.onerror = () => {
      showToast("Failed to read image", "error");
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <PhotoIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          Wallpaper
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Personalize the background -- pick a preset, paste an image URL, or upload
          your own. Saved only in this browser.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Presets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              onClick={() => apply(null)}
              className={cn(
                "h-16 rounded-lg border-2 flex items-center justify-center bg-gray-100 dark:bg-gray-800 transition-colors",
                !current
                  ? "border-blue-500"
                  : "border-transparent hover:border-gray-300 dark:hover:border-gray-700",
              )}
            >
              <span className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400">
                <XMarkIcon className="w-4 h-4" />
                None
              </span>
            </button>
            {WALLPAPER_PRESETS.map((preset) => {
              const selected = current?.type === "preset" && current.value === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => apply({ type: "preset", value: preset.id })}
                  title={preset.label}
                  className={cn(
                    "relative h-16 rounded-lg border-2 overflow-hidden transition-colors",
                    selected
                      ? "border-blue-500"
                      : "border-transparent hover:border-gray-300 dark:hover:border-gray-700",
                  )}
                  style={{ background: preset.css }}
                >
                  {selected && (
                    <span className="absolute top-1 right-1 bg-white/90 dark:bg-gray-900/90 rounded-full p-0.5">
                      <CheckIcon className="w-3 h-3 text-blue-600" />
                    </span>
                  )}
                  <span className="absolute bottom-0 inset-x-0 bg-black/40 text-white text-[11px] py-0.5 text-center">
                    {preset.label}
                  </span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Image URL</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/photo.jpg"
            />
            <Button onClick={handleUrlApply} disabled={!urlInput.trim()}>
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Upload Image</CardTitle>
        </CardHeader>
        <CardContent>
          <Label
            htmlFor="wallpaper-upload"
            className="flex items-center justify-center gap-2 h-16 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <PhotoIcon className="w-5 h-5" />
            Choose an image (max 3MB)
          </Label>
          <input
            id="wallpaper-upload"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </CardContent>
      </Card>
    </div>
  );
};
